import { useRef } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { enqueue, generateTempId } from '../lib/offline/offlineDb';
import { isNetworkOnline } from '../lib/connectivity';

/**
 * Context handed from `onMutate` to the later callbacks. `tempId` is filled in
 * by this hook so consumers can reconcile their optimistic row with the server
 * response — or, when the operation was queued offline and no server id exists,
 * keep working with the optimistic one (VIVATISPDB-443).
 */
export interface OfflineMutationContext {
  previous?: unknown;
  tempId?: string | null;
}

interface UseOfflineMutationOptions<TVariables, TData = unknown> {
  operationType: string;
  feature: string;
  mutationFn: (variables: TVariables) => Promise<TData>;
  toPayload: (variables: TVariables) => Record<string, unknown>;
  onMutate?: (variables: TVariables, tempId: string | null) => Promise<OfflineMutationContext | void>;
  onSuccess?: (data: TData | void, variables: TVariables, context: OfflineMutationContext | void) => void;
  onError?: (err: Error, variables: TVariables, context: OfflineMutationContext | void) => void;
  onSettled?: (variables: TVariables) => void;
  invalidateKeys?: QueryKey[];
  needsTempId?: boolean;
}

export function useOfflineMutation<TVariables, TData = unknown>({
  operationType,
  feature,
  mutationFn,
  toPayload,
  onMutate,
  onSuccess,
  onError,
  onSettled,
  invalidateKeys = [],
  needsTempId = false,
}: UseOfflineMutationOptions<TVariables, TData>) {
  const queryClient = useQueryClient();
  const pendingTempIdRef = useRef<string | null>(null);

  return useMutation<TData | void, Error, TVariables, OfflineMutationContext>({
    mutationFn: async (variables) => {
      // Use the tempId that was already generated in onMutate (if any)
      const tempId = pendingTempIdRef.current ?? (needsTempId ? generateTempId() : null);
      pendingTempIdRef.current = null;

      if (!isNetworkOnline()) {
        await enqueue(feature, operationType, toPayload(variables), tempId);
        return undefined;
      }

      try {
        return await mutationFn(variables);
      } catch (error) {
        // navigator.onLine can claim "online" while the network is actually
        // down — e.g. a cold start in an already-offline browser never fires
        // an offline event, lie-fi, captive portals (VIVATISPDB-435). A
        // network-level failure (fetch TypeError) means the backend was never
        // reached, so the operation is safe to queue instead of failing.
        // HTTP errors (ApiError) mean the backend answered — rethrow those.
        if (error instanceof TypeError) {
          await enqueue(feature, operationType, toPayload(variables), tempId);
          return undefined;
        }
        throw error;
      }
    },
    onMutate: async (variables) => {
      // Generate tempId upfront so onMutate and mutationFn share the same
      // one. Always generated (not only when offline) so the optimistic
      // row keeps the same id as the queued operation when a seemingly
      // online request falls back to the queue.
      const tempId = needsTempId ? generateTempId() : null;
      pendingTempIdRef.current = tempId;
      const context = await onMutate?.(variables, tempId);
      return { ...context, tempId };
    },
    onSuccess,
    onError,
    onSettled: (_data, _err, variables) => {
      if (isNetworkOnline()) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      onSettled?.(variables);
    },
  });
}
